<template>
  <div class="login-form p-8!">
    <div
      v-show="loading"
      class="absolute w-full h-full rounded-bulky bg-surface z-100 top-0 left-0 flex justify-center"
    >
      <Spinner width="100" class="fill-brand" />
    </div>

    <div class="text-xxl font-bold mb-2">
      {{ $t('ui.auth.select_account_title', { name: userName }) }}
    </div>
    <p class="text-secondary">
      {{ $t('ui.auth.continue_prompt') }}
    </p>

    <div class="mt-4">
      <!-- Admin View section -->
      <template v-if="adminEntitiesGrouped.length > 0">
        <div class="text-secondary font-semibold mb-4">
          {{ $t('ui.auth.select_admin_space') }}
        </div>
        <div class="space-y-2">
          <AdminEntityGroup
            v-for="group in adminEntitiesGrouped"
            :key="group.root.id"
            :root="group.root"
            :entities="group.entities"
            :expanded="expandedRootId === group.root.id"
            @toggle="toggleRoot(group.root.id)"
            @select="startAdminSession"
          />
        </div>
      </template>

      <!-- Separator -->
      <div
        v-if="
          adminEntitiesGrouped.length > 0 &&
          (hasAnyClientRole || hasAnyReferralRole)
        "
        class="flex items-center my-3"
      >
        <div class="grow border-t border-outline"></div>
        <span class="px-3 text-secondary text-sm">{{
          $t('ui.common.or')
        }}</span>
        <div class="grow border-t border-outline"></div>
      </div>

      <!-- Client View option -->
      <AccountOption
        v-if="hasAnyClientRole"
        id="enter-client-view"
        @click="startClientSession"
      >
        <template #icon>
          <div
            class="w-7 h-7 rounded-full bg-blue-alpha flex items-center justify-center mr-3 shrink-0"
          >
            <SvgIcon icon="User" class="text-brand" size="sm" />
          </div>
        </template>
        {{ $t('ui.auth.client_view') }}
      </AccountOption>

      <!-- Referral View option -->
      <AccountOption
        v-if="hasAnyReferralRole"
        id="enter-referral-view"
        @click="startReferralSession"
      >
        <template #icon>
          <div
            class="w-7 h-7 rounded-full bg-blue-alpha flex items-center justify-center mr-3 shrink-0"
          >
            <SvgIcon icon="UserCoins" class="text-brand" size="sm" />
          </div>
        </template>
        {{ $t('ui.auth.referral_view') }}
      </AccountOption>

      <!-- Assistant section divider -->
      <div
        v-if="
          hasAssistantRelation &&
          (adminEntitiesGrouped.length > 0 ||
            hasAnyClientRole ||
            hasAnyReferralRole)
        "
        class="flex items-center my-3"
      >
        <div class="grow border-t border-outline"></div>
        <span class="px-3 text-secondary text-sm">{{
          $t('ui.common.or')
        }}</span>
        <div class="grow border-t border-outline"></div>
      </div>

      <!-- Assistant Clients section -->
      <template v-if="hasAssistantRelation">
        <div class="text-secondary font-semibold mb-2">
          {{ $t('ui.auth.assistant_clients') }}
        </div>
        <AccountOption
          v-for="client in assistantClientsWithEntity"
          :key="client.relation_id"
          @click="startAssistantClientSession(client)"
        >
          <template #icon>
            <div
              class="w-7 h-7 rounded-full bg-blue-alpha flex items-center justify-center mr-3 shrink-0"
            >
              <SvgIcon icon="UserCog" class="text-brand" size="sm" />
            </div>
          </template>
          {{ getTextFromRootUser(client.root_user) }} -
          {{ client.entity_name }}
        </AccountOption>
      </template>
    </div>

    <hr class="my-6 border-t border-outline -mx-8" />

    <div>
      <Checkbox
        v-model="rememberSelectedSpace"
        :label="$t('ui.auth.remember_selected_space')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import UserService from '@/services/user/user'
import Spinner from '@/assets/loaders/Spinner.svg?component'
import Checkbox from '@/components/controls/Checkbox/Checkbox.vue'
import SvgIcon from '@/components/misc/SvgIcon'
import AdminEntityGroup from './AdminEntityGroup.vue'
import AccountOption from './AccountOption.vue'
import { getTextFromRootUser } from '@/helpers/entity'
import type {
  AssistantClientFlatItem,
  Entity,
  Root,
} from '@/types/services/root'
import type { SessionRole } from '@/types/services/user'
import type { UserSettings } from '@/types/services/user_settings'

interface AdminEntitiesGroup {
  root: Root
  entities: Entity[]
}

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const loading = ref(false)
const rememberSelectedSpace = ref(false)
const expandedRootId = ref<string | null>(null)

const {
  access_data,
  user,
  hasAnyClientRole,
  hasAnyReferralRole,
  hasAssistantRelation,
  assistantClientsWithEntity,
  findRootIdByEntityId,
} = storeToRefs(userStore)

const userName = computed(() => {
  if (!user.value) {
    return ''
  }

  return `${user.value.first_name} ${user.value.last_name}`
})

const adminEntitiesGrouped = computed<AdminEntitiesGroup[]>(() => {
  const groups: Map<string, AdminEntitiesGroup> = new Map()
  for (const rad of access_data.value) {
    const adminEntities: Entity[] = []
    for (const ead of rad.entity_access_list) {
      const hasAdminRole =
        rad.root_user.is_superadmin ||
        ead.relations.some((r) => r.role === 'admin')
      if (hasAdminRole) {
        adminEntities.push(ead.entity)
      }
    }
    if (adminEntities.length > 0) {
      groups.set(rad.root.id, {
        root: rad.root,
        entities: adminEntities.sort((a, b) => a.name.localeCompare(b.name)),
      })
    }
  }
  return Array.from(groups.values()).sort((a, b) =>
    a.root.name.localeCompare(b.root.name)
  )
})

function toggleRoot(rootId: string) {
  expandedRootId.value = expandedRootId.value === rootId ? null : rootId
}

onMounted(() => {
  if (!user.value) {
    router.push({ name: 'SignIn' })
    return
  }

  const lastSession = UserService.getLastSession()
  if (lastSession?.entity_id) {
    const rootId = findRootIdByEntityId.value(lastSession.entity_id)
    if (rootId) {
      expandedRootId.value = rootId
      return
    }
  }

  if (adminEntitiesGrouped.value.length === 1) {
    expandedRootId.value = adminEntitiesGrouped.value[0].root.id
  }
})

async function startSessionWithRole(role: SessionRole, entityId?: string) {
  if (loading.value) {
    return
  }

  loading.value = true

  const settings: UserSettings['additional'] = {
    privacy_mode: route.query.privacy == 'true',
  }

  if (rememberSelectedSpace.value) {
    settings.default_session = entityId
      ? { role, entity_id: entityId }
      : { role }
  }

  try {
    await userStore.saveUserAdditionalSettings(settings)
    userStore.changeSession({ role, entity_id: entityId })
    await router.push('/')
  } catch (err) {
    console.error('Failed to start session:', err)
    loading.value = false
  }
}

function startAdminSession(entityId: string) {
  startSessionWithRole('admin', entityId)
}

function startClientSession() {
  startSessionWithRole('client')
}

function startReferralSession() {
  startSessionWithRole('referral')
}

async function startAssistantClientSession(client: AssistantClientFlatItem) {
  if (loading.value) return

  loading.value = true

  try {
    userStore.changeSession({
      role: 'assistant',
      entity_id: client.fund_entity_id,
      client_relation_id: client.relation_id,
    })
    await router.push({
      name: 'AssistantFundDetail',
      params: { client_relation_id: client.relation_id },
    })
  } catch (err) {
    console.error('Failed to start assistant session:', err)
    loading.value = false
  }
}
</script>
